import * as React from 'react';
import {tokens, useSize} from '@packages/utilities/styled';

import Divider from '../../Divider';
import Text from '../../Text';
import View from '../../View';

import {getDividerHeight} from './_utils';

export const AutocompleteNote = React.memo(
  React.forwardRef(
    (
      {
        children,
        setNoteHeight,
      }: {children: React.ReactNode; setNoteHeight: (height: number) => void},
      ref: React.ForwardedRef<HTMLDivElement>
    ) => {
      const size = useSize();

      const updateNoteHeight = React.useCallback(
        (entries: ResizeObserverEntry[]) => {
          const noteElement = entries[0];
          if (noteElement) {
            const noteHeight =
              getDividerHeight(size) + noteElement.contentRect.height;
            setNoteHeight(noteHeight);
          }
        },
        [setNoteHeight, size]
      );

      /**
       * Ideally we would run this resize observer in useAutocompleteNote hook.
       * Unfortunately, that hook runs during `AutocompleteListBox` render, which means that noteRef.current is null.
       *
       * It would be great if we could just have a callback ref that we add to the AutocompleteNote body.
       * Unfortunately, callback refs do not have a cleanup mechanism to detach listeners.
       * @see https://github.com/facebook/react/issues/15176
       */
      React.useEffect(() => {
        if (ref && typeof ref === 'object' && ref.current) {
          const observer = new ResizeObserver(updateNoteHeight);
          observer.observe(ref.current);
          return () => {
            setNoteHeight(0);
            observer.disconnect();
          };
        }
      }, [updateNoteHeight, ref, setNoteHeight]);

      return (
        <>
          <Divider spacing={size === 'x-large' ? 2 : 1} />
          <View
            display="flex"
            padding={tokens.ui.autocomplete.option.padding}
            ref={ref}
          >
            <Text colorVariant="secondary">{children}</Text>
          </View>
        </>
      );
    }
  )
);

AutocompleteNote.displayName = 'AutocompleteNote';

export default AutocompleteNote;
